import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { initGA4, trackPageView } from './utils/analytics';
import { initRnkCookieBridge } from './utils/rnkcookieBridge';
import { HelmetProvider } from 'react-helmet-async';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { TrialProvider } from './contexts/TrialContext';
import TrialUpgradeFab from './components/trial/TrialUpgradeFab';
import { DashboardProvider } from './contexts/DashboardContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { SystemAlertProvider } from './contexts/SystemAlertContext';
import { NotificationProvider } from './contexts/NotificationContext';
import SystemAlertBar from './components/ui/SystemAlertBar';
import ProtectedRoute from './components/auth/ProtectedRoute';

import { SentryErrorBoundary } from './components/ui/SentryErrorBoundary';
import ChunkErrorBoundary from './components/ui/ChunkErrorBoundary';
import { KeyboardShortcutsModalManager } from './components/ui';
import DesktopOnlyGate from './components/ui/DesktopOnlyGate';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import SessionManager from './components/auth/SessionManager';
import { FEATURE_FLAGS } from './config/featureFlags';
import { Loader2 } from 'lucide-react';

const MobileApp = lazy(() => import('./mobile/MobileApp'));

// Import checkbox styles AFTER Tailwind to override native rendering
import './styles/checkbox.css';

// Auto-retry lazy imports: on chunk 404 after deploy, reload once to get new manifest
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error: Error) => {
      // Only handle chunk load failures (deploy invalidated old hashes)
      if (
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed') ||
        error.message.includes('error loading dynamically imported module')
      ) {
        const key = 'chunk_reload_ts';
        const last = sessionStorage.getItem(key);
        const now = Date.now();
        // Reload once per 30s to avoid infinite loops
        if (!last || now - Number(last) > 30000) {
          sessionStorage.setItem(key, String(now));
          window.location.reload();
          // Return a never-resolving promise to prevent React from rendering stale UI
          return new Promise(() => {});
        }
      }
      throw error;
    })
  );
}

// Lazy load components for better performance and code splitting
const DashboardLayout = lazyWithRetry(() => import('./layouts/DashboardLayout'));
const OverviewPage = lazyWithRetry(() => import('./pages/OverviewPage'));
const GoogleBusinessPage = lazyWithRetry(() => import('./pages/GoogleBusinessPage'));
const ProjectDashboardPage = lazyWithRetry(() => import('./pages/ProjectDashboardPage'));
const MissionControlView = lazyWithRetry(() => import('./pages/MissionControlView'));
const SEOAnalysisPage = lazyWithRetry(() => import('./pages/SEOAnalysisPage'));
const KnowledgeGraphPage = lazyWithRetry(() => import('./pages/KnowledgeGraphPage'));
const SavedKnowledgeGraphContentIdeas = lazyWithRetry(() => import('./pages/SavedKnowledgeGraphContentIdeas'));
const AllKnowledgeGraphContentIdeas = lazyWithRetry(() => import('./pages/AllKnowledgeGraphContentIdeas'));
const KeywordResearchPage = lazyWithRetry(() => import('./pages/KeywordResearchPage'));
const KeywordsPage = lazyWithRetry(() => import('./pages/KeywordsPage'));
const APIConnectionsPage = lazyWithRetry(() => import('./pages/keywords/APIConnectionsPage'));
const CompetitorAnalysisPage = lazyWithRetry(() => import('./pages/CompetitorAnalysisPage'));
const GeographicAnalyticsPage = lazyWithRetry(() => import('./pages/GeographicAnalyticsPage'));
const GeoHubPage = lazyWithRetry(() => import('./pages/GeoHubPage'));
const ABTestingPage = lazyWithRetry(() => import('./pages/ABTestingPage'));
const BrandGuidelinesPage = lazyWithRetry(() => import('./pages/BrandGuidelinesPage'));
const AccountSettingsPage = lazyWithRetry(() => import('./pages/AccountSettingsPage'));
const AllAnalysesPage = lazyWithRetry(() => import('./pages/AllAnalysesPage'));
const IssuesManagementPage = lazyWithRetry(() => import('./pages/IssuesManagementPage'));
const AccessibilityPage = lazyWithRetry(() => import('./pages/AccessibilityPage'));
const ApiRoutesPage = lazyWithRetry(() => import('./pages/ApiRoutesPage'));
const TodosPage = lazyWithRetry(() => import('./pages/TodosPage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const LighthouseTestPage = lazyWithRetry(() => import('./pages/LighthouseTestPage'));
const OAuthCallbackPage = lazyWithRetry(() => import('./pages/OAuthCallbackPage'));
const RealTimeMonitoringPage = lazyWithRetry(() => import('./pages/RealTimeMonitoringPage'));
const GeoOptimizationDocsPage = lazyWithRetry(() => import('./pages/GeoOptimizationDocsPage'));
const AIContentPage = lazyWithRetry(() => import('./pages/AIContentPage'));
const ContentStudioPage = lazyWithRetry(() => import('./pages/ContentStudioPage'));
const StrategyEnginePage = lazyWithRetry(() => import('./pages/StrategyEnginePage'));
const AutopilotPage = lazyWithRetry(() => import('./pages/AutopilotPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const OnboardingWizard = lazyWithRetry(() => import('./pages/OnboardingWizard'));

// Loading component for Suspense fallback
const LoadingFallback: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div className="flex items-center justify-center min-h-[100dvh] bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400 text-sm">{message}</p>
    </div>
  </div>
);

// Route-specific loading messages for better UX
const RouteLoadingFallback: React.FC<{ routeName: string }> = ({ routeName }) => (
  <LoadingFallback message={`Loading ${routeName}...`} />
);

// Keyboard shortcuts integration component
const KeyboardShortcutsIntegration: React.FC = () => {
  useKeyboardShortcuts(); // Initialise keyboard shortcuts
  return <KeyboardShortcutsModalManager />;
};

/** Hide system health alerts on public auth pages — don't scare off conversions */
function RouteAwareAlertBar() {
  const { pathname } = useLocation();
  const isAuthRoute = pathname.startsWith('/login') || pathname.startsWith('/register') || pathname.startsWith('/onboarding');
  if (isAuthRoute) return null;
  return <SystemAlertBar />;
}

/** Mounts the RnkCookie bridge once (RnkCookie owns the banner UI).
 *  Sits above the mobile/desktop split so both surfaces get the CMP. */
function RnkCookieBridgeMount() {
  useEffect(() => { initRnkCookieBridge(); }, []);
  return null;
}

/** GA4 route change tracker — consent handled natively by RnkCookie */
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => { initGA4(); }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location]);
  return null;
}

/** Legacy redirect: /ai-content?title=x&keywords=y → /content-studio/draft?title=x&keywords=y */
function LegacyAIContentRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/content-studio/draft${search}${hash}`} replace />;
}

function App() {
  const isMobileDevice = useMediaQuery({ maxWidth: 1023 });

  return (
    <HelmetProvider>
      <DndProvider backend={HTML5Backend}>
        <ThemeProvider>
            <AuthProvider>
              <TrialProvider>
              <NotificationProvider>
              <ProjectProvider>
                <DashboardProvider>
                  <SystemAlertProvider pollInterval={60000}>
                  <BrowserRouter>
                  <DesktopOnlyGate>
                  <SentryErrorBoundary>
                  <ChunkErrorBoundary>
                    <TrialUpgradeFab />
                    <RouteAwareAlertBar />
                    <RnkCookieBridgeMount />
                    <KeyboardShortcutsIntegration />
                    <SessionManager />
                    <AnalyticsRouteTracker />
                    {isMobileDevice ? (
                      <Suspense fallback={<div className="min-h-screen bg-gray-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>}>
                        <MobileApp />
                      </Suspense>
                    ) : (
                    <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Login" />}>
                        <LoginPage />
                      </Suspense>
                    } />
                    <Route path="/register" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Registration" />}>
                        <LoginPage />
                      </Suspense>
                    } />
                    <Route path="/forgot-password" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Password Reset" />}>
                        <ForgotPasswordPage />
                      </Suspense>
                    } />
                    <Route path="/reset-password" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Password Reset" />}>
                        <ResetPasswordPage />
                      </Suspense>
                    } />
                    {import.meta.env.DEV && (
                    <Route path="/lighthouse-test" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Lighthouse Test" />}>
                        <LighthouseTestPage />
                      </Suspense>
                    } />
                    )}
                    <Route path="/keywords/oauth/callback" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="OAuth Callback" />}>
                        <OAuthCallbackPage />
                      </Suspense>
                    } />

                    {/* Legacy onboarding route: always route to enhanced wizard */}
                    <Route path="/onboarding" element={<Navigate to="/onboarding/enhanced" replace />} />

                    <Route path="/onboarding/enhanced" element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoadingFallback routeName="Onboarding" />}>
                          <OnboardingWizard />
                        </Suspense>
                      </ProtectedRoute>
                    } />

                    {/* Protected Routes */}
                    <Route path="/" element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoadingFallback routeName="RnkRocket" />}>
                          <DashboardLayout />
                        </Suspense>
                      </ProtectedRoute>
                    }>
                      {/* Home: with Mission Control live, the root route IS Mission Control —
                          an already-authenticated visit to app.rnkrocket.com must land there,
                          not just the post-login redirect (Sam, 2026-07-11). The Overview
                          (classic dashboard) lives at /overview; flag off restores it here. */}
                      <Route index element={
                        FEATURE_FLAGS.MISSION_CONTROL ? (
                          <Navigate to="/mission-control" replace />
                        ) : (
                          <Suspense fallback={<RouteLoadingFallback routeName="Overview" />}>
                            <OverviewPage />
                          </Suspense>
                        )
                      } />
                      <Route path="overview" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Overview" />}>
                          <OverviewPage />
                        </Suspense>
                      } />
                      {/* Mission Control — flagship landing (docs/plans/plan-mission-control.md).
                          Reachable route always registered; the sidebar item + default-landing
                          redirect are flag-gated (FEATURE_FLAGS.MISSION_CONTROL), so with the
                          flag off nothing links here. */}
                      <Route path="mission-control" element={
                        FEATURE_FLAGS.MISSION_CONTROL ? (
                          <Suspense fallback={<RouteLoadingFallback routeName="Mission Control" />}>
                            <MissionControlView />
                          </Suspense>
                        ) : (
                          <Navigate to="/" replace />
                        )
                      } />
                      <Route path="projects/:projectId" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Project Dashboard" />}>
                          <ProjectDashboardPage />
                        </Suspense>
                      } />
                      <Route path="google-business" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Google Business Profile" />}>
                          <GoogleBusinessPage />
                        </Suspense>
                      } />
                      <Route path="seo-analysis" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="SEO Analysis" />}>
                          <SEOAnalysisPage />
                        </Suspense>
                      } />
                      <Route path="analysis/:analysisId" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Analysis Details" />}>
                          <SEOAnalysisPage />
                        </Suspense>
                      } />
                      <Route path="analyses" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="All Analyses" />}>
                          <AllAnalysesPage />
                        </Suspense>
                      } />
                      <Route path="issues" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Issues Management" />}>
                          <IssuesManagementPage />
                        </Suspense>
                      } />
                      <Route path="accessibility" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Accessibility" />}>
                          <AccessibilityPage />
                        </Suspense>
                      } />
                      {import.meta.env.DEV && (
                        <Route path="api-routes" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="API Routes" />}>
                            <ApiRoutesPage />
                          </Suspense>
                        } />
                      )}
                      <Route path="todos" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="TODOs" />}>
                          <TodosPage />
                        </Suspense>
                      } />
                      <Route path="knowledge-graph" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Knowledge Graph" />}>
                            <KnowledgeGraphPage />
                          </Suspense>
                      } />
                      <Route path="knowledge-graph/:graphId" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Knowledge Graph Details" />}>
                            <KnowledgeGraphPage />
                          </Suspense>
                      } />
                      <Route path="knowledge-graph/:graphId/saved-content-ideas" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Saved Content Ideas" />}>
                            <SavedKnowledgeGraphContentIdeas />
                          </Suspense>
                      } />
                      <Route path="knowledge-graph/:graphId/all-content-ideas" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="All Content Ideas" />}>
                            <AllKnowledgeGraphContentIdeas />
                          </Suspense>
                      } />
                      <Route path="keyword-research" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Keyword Research" />}>
                          <KeywordResearchPage />
                        </Suspense>
                      } />
                      <Route path="keywords/:tabId?" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Keywords" />}>
                          <KeywordsPage />
                        </Suspense>
                      } />
                      <Route path="strategy-engine" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Strategy Engine" />}>
                            <StrategyEnginePage />
                          </Suspense>
                      } />
                      <Route path="strategy-engine/:section" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Strategy Engine" />}>
                            <StrategyEnginePage />
                          </Suspense>
                      } />
                      <Route path="geo" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="GEO Hub" />}>
                            <GeoHubPage />
                          </Suspense>
                      } />
                      <Route path="geo/:section" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="GEO Hub" />}>
                            <GeoHubPage />
                          </Suspense>
                      } />
                      <Route path="autopilot" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Autopilot" />}>
                            <AutopilotPage />
                          </Suspense>
                      } />
                      {/* Keywords tabs remodel — legacy keyword tools that now live in Strategy Engine */}
                      <Route path="keywords/clustering" element={<Navigate to="/strategy-engine/clusters" replace />} />
                      <Route path="keywords/content-briefs" element={<Navigate to="/strategy-engine" replace />} />
                      {/* Redirects from old Content Intelligence URLs */}
                      <Route path="keywords/content-intelligence" element={<Navigate to="/strategy-engine" replace />} />
                      <Route path="keywords/content-intelligence/clusters" element={<Navigate to="/strategy-engine/clusters" replace />} />
                      <Route path="keywords/content-intelligence/geo" element={<Navigate to="/strategy-engine/geo" replace />} />
                      <Route path="keywords/content-intelligence/calendar" element={<Navigate to="/strategy-engine/calendar" replace />} />
                      <Route path="keywords/content-intelligence/opportunities" element={<Navigate to="/strategy-engine/opportunities" replace />} />
                      {/* Content Studio — premium tab shell */}
                      <Route path="content-studio" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Content Studio" />}>
                            <ContentStudioPage />
                          </Suspense>
                      } />
                      <Route path="content-studio/:tab" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Content Studio" />}>
                            <ContentStudioPage />
                          </Suspense>
                      } />
                      {/* Backward compat: /ai-content → /content-studio/draft (preserves query + hash) */}
                      <Route path="ai-content" element={<LegacyAIContentRedirect />} />
                      <Route path="api-connections" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="API Connections" />}>
                          <APIConnectionsPage />
                        </Suspense>
                      } />
                      <Route path="competitor-analysis" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Competitor Analysis" />}>
                            <CompetitorAnalysisPage />
                          </Suspense>
                      } />
                      {FEATURE_FLAGS.GEOGRAPHIC && (
                        <Route path="geographic-analytics" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Geographic Analytics" />}>
                            <GeographicAnalyticsPage />
                          </Suspense>
                        } />
                      )}
                      {FEATURE_FLAGS.AB_TESTING && (
                        <Route path="ab-testing" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="A/B Testing" />}>
                            <ABTestingPage />
                          </Suspense>
                        } />
                      )}
                      <Route path="brand-guidelines" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Brand Guidelines" />}>
                          <BrandGuidelinesPage />
                        </Suspense>
                      } />
                      <Route path="settings" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="Account Settings" />}>
                          <AccountSettingsPage />
                        </Suspense>
                      } />
                      <Route path="projects/:projectId/real-time-monitoring" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Real-Time Monitoring" />}>
                            <RealTimeMonitoringPage />
                          </Suspense>
                      } />
                      <Route path="real-time-monitoring" element={
                          <Suspense fallback={<RouteLoadingFallback routeName="Real-Time Monitoring" />}>
                            <RealTimeMonitoringPage />
                          </Suspense>
                      } />
                      <Route path="docs/geo-optimization" element={
                        <Suspense fallback={<RouteLoadingFallback routeName="GEO Documentation" />}>
                          <GeoOptimizationDocsPage />
                        </Suspense>
                      } />
                    </Route>

                    {/* 404 Catch-all Route */}
                    <Route path="*" element={
                      <Suspense fallback={<RouteLoadingFallback routeName="Page" />}>
                        <NotFoundPage />
                      </Suspense>
                    } />
                    </Routes>
                    )}
                  </ChunkErrorBoundary>
                  </SentryErrorBoundary>
                  </DesktopOnlyGate>
                </BrowserRouter>
                  </SystemAlertProvider>
              </DashboardProvider>
            </ProjectProvider>
              </NotificationProvider>
              </TrialProvider>
            </AuthProvider>
        </ThemeProvider>
      </DndProvider>
    </HelmetProvider>
  );
}

export default App;
